<template>
    <v-container>
        <page-header :title="`${mode === 'edit' ? 'Edit' : mode === 'show' ? '' : 'New'} Article`.trim()" icon="mdi-newspaper-variant" />
        <v-card class="form-card" elevation="1">
            <v-form :readonly="readOnly">
                <div class="form-section-title">
                    <v-icon small>mdi-text-box-outline</v-icon>Details
                </div>
                <v-row>
                    <v-col cols="12" md="8">
                        <v-text-field
                            v-model="article.title"
                            outlined
                            dense
                            hide-details="auto"
                            label="Title"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <date-time-picker
                            :date-label="'Article Date'"
                            :date="articleDateTime"
                            @setDate="setArticleDatetime"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-image-multiple-outline</v-icon>Media
                </div>
                <v-row>
                    <v-col cols="12" md="6">
                        <image-upload
                            v-model="image"
                            :src="imagePath"
                            label="Cover image"
                            :readonly="readOnly"
                            @change="changeImage"
                            @clear="clearImage"
                        />
                    </v-col>
                    <v-col v-if="!readOnly" cols="12" md="6">
                        <div class="text-caption article-file-label">Attachment</div>
                        <div class="d-flex align-center">
                            <v-file-input
                                v-model="file"
                                outlined
                                dense
                                hide-details="auto"
                                accept="pdf/*"
                                show-size
                                label="Article File"
                                prepend-icon=""
                                prepend-inner-icon="mdi-paperclip"
                                :clearable="clearable"
                                @change="changeFile"
                                @click:clear="clearFile()"
                            />
                            <v-btn
                                class="ml-2 white--text"
                                depressed
                                color="black"
                                :disabled="!file"
                                @click="uploadFile()"
                            >
                                <v-icon left small>mdi-upload</v-icon>Upload
                            </v-btn>
                        </div>
                        <div class="text-caption grey--text mt-2">
                            Upload a file to get a link you can use inside the content.
                        </div>
                        <v-text-field
                            v-if="fileUrl"
                            :value="fileUrl"
                            class="mt-3"
                            outlined
                            dense
                            readonly
                            hide-details
                            label="File link"
                            prepend-inner-icon="mdi-link-variant"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-format-paragraph</v-icon>Content
                </div>
                <editor
                    :id="`article_content`"
                    v-model="article.content"
                    :api-key="tinymceApiKey"
                    :init="editorInit"
                />
            </v-form>
        </v-card>
        <div v-if="mode !== 'show'" class="form-actions">
            <v-spacer />
            <v-btn large text @click="$router.go(-1)">
                Cancel
            </v-btn>
            <v-btn :loading="loading" :disabled="shouldDisableSubmit()" class="white--text" large depressed color="black" @click="onSubmit">
                <v-icon left>mdi-content-save-outline</v-icon>Save
            </v-btn>
        </div>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import PageHeader from '@/components/PageHeader'
    import ImageUpload from '@/components/ImageUpload'
    import articleService from '@/services/article'
    import uploadService from '@/services/upload'
    import Alert from '@/components/Alert'
    import DateTimePicker from '@/components/DateTimePicker'
    import momentTimezone from 'moment-timezone'
    import Editor from '@tinymce/tinymce-vue'
    export default {
        name: 'ArticleForm',
        components: { Alert, DateTimePicker, Editor, PageHeader, ImageUpload },
        data: () => {
            return {
                showAlert: false,
                message: null,
                type: null,
                mode: null,
                googleApiHost: 'https://storage.googleapis.com',
                readOnly: false,
                clearable: true,
                loading: false,
                startDate: null,
                endDate: null,
                fileUrl: '',
                article: {
                },
                articleId: null,
                rules: [
                    v => !!v || 'Field is required'
                ],
                image: {
                    name: 'Image file'
                },
                imageChanged: false,
                imagePath: '',
                file: {
                    name: 'File'
                },
                filePath: '',
                articleDateTime: null,
                editorInit: {
                    images_upload_handler: function(file, success) {
                        let formData = new FormData()
                        formData.append('file', file.blob(), file.filename())
                        uploadService.uploadFile(formData).then((response) => { success(process.env.VUE_APP_GOOGLE_STORAGE_API + '/' + response.data.content) })
                    },
                    menubar: false,
                    min_height: 600,
                    plugins: [
                        'advlist autolink lists charmap print preview anchor',
                        'searchreplace visualblocks fullscreen',
                        'insertdatetime table paste code help wordcount link',
                        'image code media'
                    ],
                    automatic_uploads: true,
                    powerpaste_allow_local_images: true,
                    file_picker_types: 'image',
                    toolbar: 'undo redo | styleselect | forecolor | bold italic | fontselect | emoticons | alignleft aligncenter alignright alignjustify | numlist bullist | outdent indent | link unlink | image media | code '
                },
                tinymceApiKey: process.env.VUE_APP_TINYMCE_API_KEY
            }
        },
        async created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.articleId = this.$route.params.articleId
            if (this.articleId) {
                articleService.getArticle(this.articleId).then(resp => {
                    this.article = resp.data
                    this.articleDateTime = momentTimezone(this.article.date).tz('Europe/Athens').format('YYYY-MM-DD').toString()

                    if (this.article.image) {
                        this.imagePath = this.googleApiHost + '/' + this.article.image
                    }
                })
            }
        },
        methods: {
            changeFile() {
                this.fileChanged = true
                if (this.file) {
                    this.filePath = URL.createObjectURL(this.file)
                }
            },
            clearFile() {
                this.filePath = ''
            },
            changeImage() {
                this.imageChanged = true
                if (this.image) {
                    this.imagePath = URL.createObjectURL(this.image)
                }
            },
            clearImage() {
                this.imagePath = ''
                this.article.image = null
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            shouldDisableSubmit() {
                return false
            },
            setArticleDatetime(date) {
                this.articleDateTime = date
            },
            onEdit() {
                this.$router.push(`/articles/${this.articleId}/edit`)
            },
            createArticle() {
                articleService.createArticle(this.article).then(articleResponse => {
                    if (articleResponse.id) {
                        this.articleId = articleResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/articles`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            editArticle() {
                articleService.updateArticle(this.articleId, this.article).then(articleResponse => {
                    if (articleResponse.id) {
                        this.articleId = articleResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/articles`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            uploadFile() {
                if (!this.fileChanged || this.file == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.file)
                uploadService.uploadFile(formData).then((response) => { this.fileUrl = this.googleApiHost + '/' + response.data.content })
            },
            uploadImage() {
                if (!this.imageChanged || this.image == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.image)
                return articleService.uploadFile(formData)
            },
            async onSubmit() {
                this.loading = true
                this.article.date = momentTimezone(this.articleDateTime).tz('Europe/Athens').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]').toString()

                const uploadImageResponse = await this.uploadImage()
                const imageUrl = uploadImageResponse?.data.content || null
                if (imageUrl) {
                    this.article.image = imageUrl
                }
                if (this.mode === 'edit') {
                    this.editArticle()
                } else if (this.mode === 'create') {
                    this.createArticle()
                }
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}
</style>
<style scoped>
.article-file-label {
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 4px;
}

.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    max-width: 70%;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}
</style>
